"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ChevronRight, Shield, Globe, HelpCircle, HardHat, DollarSign, Activity } from "lucide-react";

export function Footer() {
  const pathname = usePathname();

  // Exclude footer from dashboards, bookings, and admin panels for optimal UX
  const excludedPaths = [
    "/admin",
    "/dashboard",
    "/booking",
    "/payment",
    "/contractor/dashboard",
    "/contractor/jobs",
    "/contractor/onboarding"
  ];

  const shouldHide = excludedPaths.some((path) => pathname.startsWith(path));
  if (shouldHide) return null;

  const services = [
    { name: "Plumbing", href: "/booking?category=plumbing" },
    { name: "Electrical", href: "/booking?category=electrical" },
    { name: "Carpet & Flooring", href: "/booking?category=flooring" },
    { name: "Painting Services", href: "/booking?category=painting" },
    { name: "Handyman & Assembly", href: "/booking?category=handyman" },
    { name: "Cleaning & Yard", href: "/booking?category=cleaning" }
  ];

  const company = [
    { name: "How it Works", href: "/#how-it-works" },
    { name: "Pricing Guide", href: "/pricing" },
    { name: "For Contractors", href: "/contractor/onboarding" },
    { name: "Customer Dashboard", href: "/dashboard" }
  ];

  const resources = [
    { name: "Help Center / FAQ", href: "/help" },
    { name: "Contact Us", href: "/contact" },
    { name: "Cookie Consent", href: "/cookies" },
  ];

  const legal = [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
    { name: "Cookies Policy", href: "/cookies" }
  ];

  return (
    <footer className="bg-white pt-20 pb-10 border-t border-slate-200 relative overflow-hidden text-slate-950">
      {/* Top visual gradient accent */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/60 to-transparent" />
      
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-emerald-50/70 to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 mb-20">
          
          {/* Brand Info Column */}
          <div className="col-span-1 md:col-span-2 lg:col-span-2 space-y-6">
            <Link href="/" className="brand-logo-shell group">
              <img
                src="/lastest-logo-fixer.png"
                alt="FixerLocal"
                className="brand-logo h-12 select-none transition-transform group-hover:scale-[1.03]"
              />
            </Link>
            <p className="text-slate-600 text-sm font-medium leading-relaxed max-w-sm">
              Book trusted local trades for residential and commercial work. Get an instant AI price estimate, compare bids, and pay only once the job's done.
            </p>
          </div>

          {/* Trade Services Column */}
          <div className="col-span-1">
            <h4 className="text-xs font-black text-slate-950 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
              <HardHat size={12} className="text-emerald-600" />
              Services
            </h4>
            <ul className="space-y-4">
              {services.map((item) => (
                <li key={item.name}>
                  <Link
                    href={item.href}
                    className="text-slate-600 text-sm font-semibold hover:text-emerald-700 transition-colors flex items-center gap-2 group"
                  >
                    <ChevronRight size={12} className="opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all text-emerald-600" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions Column */}
          <div className="col-span-1">
            <h4 className="text-xs font-black text-slate-950 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
              <DollarSign size={12} className="text-emerald-600" />
              Solutions
            </h4>
            <ul className="space-y-4">
              {company.map((item) => (
                <li key={item.name}>
                  <Link
                    href={item.href}
                    className="text-slate-600 text-sm font-semibold hover:text-emerald-700 transition-colors flex items-center gap-2 group"
                  >
                    <ChevronRight size={12} className="opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all text-emerald-600" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Column */}
          <div className="col-span-1">
            <h4 className="text-xs font-black text-slate-950 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
              <HelpCircle size={12} className="text-emerald-600" />
              Resources
            </h4>
            <ul className="space-y-4">
              {resources.map((item) => (
                <li key={item.name}>
                  <Link
                    href={item.href}
                    className="text-slate-600 text-sm font-semibold hover:text-emerald-700 transition-colors flex items-center gap-2 group"
                  >
                    <ChevronRight size={12} className="opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all text-emerald-600" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Compliance Column */}
          <div className="col-span-1">
            <h4 className="text-xs font-black text-slate-950 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
              <Shield size={12} className="text-emerald-600" />
              Security
            </h4>
            <ul className="space-y-4">
              {legal.map((item) => (
                <li key={item.name}>
                  <Link
                    href={item.href}
                    className="text-slate-600 text-sm font-semibold hover:text-emerald-700 transition-colors flex items-center gap-2 group"
                  >
                    <ChevronRight size={12} className="opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all text-emerald-600" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

        </div>

        {/* Bottom copyright and status bar */}
        <div className="pt-10 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-[10px] font-black uppercase tracking-widest text-slate-600">
            <Link href="/privacy" className="hover:text-emerald-700 transition-colors">Privacy Safeguards</Link>
            <Link href="/terms" className="hover:text-emerald-700 transition-colors">Terms of Dispatch</Link>
            <Link href="/cookies" className="hover:text-emerald-700 transition-colors">Cookies Preferences</Link>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-100 shadow-sm">
              <Activity size={12} className="text-emerald-600 animate-pulse" />
              <span className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">
                All Systems Operational
              </span>
            </div>
            <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
              © 2026 Automation Partners Pty Ltd
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
